<!-- CrudPedidosSeguimiento.vue — Vista de seguimiento -->
<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Seguimiento de pedidos"
      v-model="filtro"
      searchPlaceholder="# pedido o cliente..."
    >
      <template #extra>
        <CrudFechaRango
          v-model:fechaInicio="filtroFechaDesde"
          v-model:fechaFin="filtroFechaHasta"
        />
      </template>
      <template #actions>
        <button
          class="btn btn-reporte btn-header"
          @click="$refs.modalReporte.abrir()"
        >
          <i class="bi bi-bar-chart-line"></i>
          <span class="btn-header-texto ms-2">Reporte</span>
        </button>
        <button
          class="btn btn-exportar btn-header"
          @click="exportarExcel"
          :disabled="exportando"
        >
          <i :class="exportando ? 'bi bi-hourglass-split' : 'bi bi-file-earmark-excel'"></i>
          <span class="btn-header-texto ms-2">Exportar Excel</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- Tabla -->
    <PedidosTablaSeguimiento
      :pedidos="pedidosPaginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :totalRegistros="pedidosOrdenados.length"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :paginasVisibles="paginasVisibles"
      :sortKey="sortKey"
      :sortDir="sortDir"
      :porPagina="porPagina"
      :opcionesPorPagina="paginacionStore.opcionesPorPagina"
      :itemsParaFiltroEstatus="pedidosPreEstatus"
      v-model:filtroEstatusSet="filtroEstatusSet"
      @ver-detalle="$refs.modalDetalle.abrir($event)"
      @editar="abrirEditar"
      @cambiar-pagina="paginaActual = $event"
      @update:sort="onSort"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <!-- Modal detalle (solo lectura) -->
    <ModalDetallePedido ref="modalDetalle" />

    <!-- Modal editar seguimiento -->
    <ModalEditarSeguimiento ref="modalEditar" @guardado="cargarPedidos" />

    <!-- Modal reporte -->
    <ModalReportePedidos ref="modalReporte" :pedidos="pedidos" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Swal from "sweetalert2";
import { getPedidos, exportarSeguimientoPedidos } from "@/utils/api_backend";
import { ordenarPedidos } from "../TiempoReal/ordenarPedidos.js";
import { usePaginacionSeguimientoStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import CrudFechaRango from "@/components/Shared/CrudFechaRango.vue";

import PedidosTablaSeguimiento from "./PedidosTabla.vue";
import ModalDetallePedido from "../Pedidos/ModalDetallePedido.vue";
import ModalEditarSeguimiento from "./ModalEditarSeguimiento.vue";
import ModalReportePedidos from "./ModalReportePedidos.vue";

// ── Store ─────────────────────────────────────────────────
const paginacionStore = usePaginacionSeguimientoStore();

// ── Estado ───────────────────────────────────────────────
const pedidos = ref([]);
const loading = ref(false);
const exportando = ref(false);
const modalEditar = ref(null);

// ── Filtros ──────────────────────────────────────────────
const filtro = ref("");
const filtroEstatusSet = ref(new Set());
const filtroFechaDesde = ref("");
const filtroFechaHasta = ref("");

// ── Ordenamiento ─────────────────────────────────────────
const sortKey = ref("fecha_recibo");
const sortDir = ref("desc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

// ── Paginación ────────────────────────────────────────────
const paginaActual = ref(1);
const porPagina = computed(() => paginacionStore.porPagina);

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

watch([filtro, filtroEstatusSet, filtroFechaDesde, filtroFechaHasta], () => {
  paginaActual.value = 1;
});

// ── Computed ──────────────────────────────────────────────
// Texto + rango de fechas — universo que alimenta las opciones del
// embudo de Estatus almacén (para que reflejen la búsqueda/fechas
// activas, sin excluirse a sí mismo).
const pedidosPreEstatus = computed(() =>
  pedidos.value.filter((p) => {
    const q = filtro.value.toLowerCase();
    const matchTexto =
      String(p.numero_pedido).includes(q) ||
      p.cliente?.toLowerCase().includes(q) ||
      p.cliente_nombre?.toLowerCase().includes(q) ||
      p.cliente_numero?.toLowerCase().includes(q);
    const matchFechaDesde =
      !filtroFechaDesde.value || p.fecha_entrega_real >= filtroFechaDesde.value;
    const matchFechaHasta =
      !filtroFechaHasta.value || p.fecha_entrega_real <= filtroFechaHasta.value;
    return matchTexto && matchFechaDesde && matchFechaHasta;
  })
);

const pedidosFiltrados = computed(() => {
  const filtrados = pedidosPreEstatus.value.filter(
    (p) => !filtroEstatusSet.value.size || filtroEstatusSet.value.has(p.estatus_almacen)
  );

  const activos = filtrados.filter(
    (p) => p.estatus_almacen !== "Entregado" && p.estatus_almacen !== "Cancelado"
  );
  const entregados = filtrados.filter((p) => p.estatus_almacen === "Entregado");
  const cancelados = filtrados.filter((p) => p.estatus_almacen === "Cancelado");

  return [...ordenarPedidos(activos), ...ordenarPedidos(entregados), ...cancelados];
});

const pedidosOrdenados = computed(() => {
  return [...pedidosFiltrados.value].sort((a, b) => {
    let valA = a[sortKey.value] ?? "";
    let valB = b[sortKey.value] ?? "";
    const cmp = String(valA).localeCompare(String(valB), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(pedidosOrdenados.value.length / porPagina.value))
);
const pedidosPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return pedidosOrdenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  pedidosOrdenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, pedidosOrdenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

// ── Carga ─────────────────────────────────────────────────
async function cargarPedidos() {
  loading.value = true;
  try {
    pedidos.value = await getPedidos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los pedidos.", "error");
  } finally {
    loading.value = false;
  }
}

// ── Editar ────────────────────────────────────────────────
function abrirEditar(pedido) {
  modalEditar.value.abrir(pedido);
}

// ── Exportar Excel ────────────────────────────────────────
async function exportarExcel() {
  const hoy = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const hoyStr = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`;
  const primerDia = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-01`;

  const { value: fechas, isConfirmed } = await Swal.fire({
    title: "Exportar seguimiento a Excel",
    html: `
      <div style="text-align:left;margin-bottom:8px">
        <label style="font-size:0.85rem;font-weight:700;color:var(--d-green);display:block;margin-bottom:4px">
          Fecha inicio (fecha recibo)
        </label>
        <input id="swal-fecha-inicio" type="date" value="${primerDia}"
          class="swal2-input" style="margin:0;width:100%">
      </div>
      <div style="text-align:left;margin-top:12px">
        <label style="font-size:0.85rem;font-weight:700;color:var(--d-green);display:block;margin-bottom:4px">
          Fecha fin (fecha recibo)
        </label>
        <input id="swal-fecha-fin" type="date" value="${hoyStr}"
          class="swal2-input" style="margin:0;width:100%">
      </div>
    `,
    confirmButtonText: '<i class="bi bi-download me-1"></i>Descargar',
    confirmButtonColor: "var(--d-green-dark)",
    cancelButtonText: "Cancelar",
    showCancelButton: true,
    preConfirm: () => {
      const inicio = document.getElementById("swal-fecha-inicio").value;
      const fin = document.getElementById("swal-fecha-fin").value;
      if (!inicio || !fin) {
        Swal.showValidationMessage("Ambas fechas son obligatorias");
        return false;
      }
      if (inicio > fin) {
        Swal.showValidationMessage("La fecha inicio no puede ser mayor a la fecha fin");
        return false;
      }
      return { inicio, fin };
    },
  });

  if (!isConfirmed || !fechas) return;

  exportando.value = true;
  try {
    await exportarSeguimientoPedidos(fechas.inicio, fechas.fin);
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo Excel.", "error");
  } finally {
    exportando.value = false;
  }
}

// ── Mount ─────────────────────────────────────────────────
onMounted(async () => {
  await cargarPedidos();
});
</script>

<style scoped>
.btn-reporte {
  background-color: #bc6c25;
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-reporte:hover {
  background-color: #9a5418;
  color: var(--d-cream);
}

.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-exportar:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
